import React, { useState } from 'react';
import { deleteGuideMedia } from '../../../../api/stays-guide-client';
import { StaysApiError } from '../../../../api/stays-client';
import type { GuideFixedSlot, GuideMedia } from '../../../../interfaces/stays-guide';
import { AppButton } from '../../../button/app-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { StatusPill } from '../../../status-pill/status-pill';
import { ProgressFill, ProgressTrack } from '../../../gallery/entity-gallery.styled';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import toast from '../../../../utils/toast';
import { FileButton, HiddenFile, Muted, Row, Section, Thumb } from './guide-editor.styled';
import { EmptyFrame, Frame, Progress, SlotCard, SlotGrid, SlotName } from './guide-photos-editor.styled';
import { FIXED_SLOTS, isAfterCheckinSlot, slotLabel } from './guide-model';
import { ACCEPT_PHOTOS, MediaUpdate, uploadCompressed, uploadErrorText } from './guide-upload';

/**
 * THE PHOTOS TAB (plan S7.8): the guide's eight fixed slots — Cover, Host
 * photo, Step 01 … Step 06. A slot holds one photo; uploading again REPLACES
 * it (the server swaps the row). Each upload is compressed on the device
 * first (utils/compress-image.ts) and shows a determinate progress bar.
 * Steps 3–6 are released with the door codes, and say so.
 */

export interface GuidePhotosEditorProps {
  listingId: string;
  media: GuideMedia[];
  canManage: boolean;
  onMediaChange: MediaUpdate;
}

export const GuidePhotosEditor: React.FC<GuidePhotosEditorProps> = ({ listingId, media, canManage, onMediaChange }) => {
  const [progress, setProgress] = useState<Partial<Record<GuideFixedSlot, number>>>({});
  const [removing, setRemoving] = useState<GuideFixedSlot | null>(null);

  const setSlotProgress = (slot: GuideFixedSlot, value: number | null) =>
    setProgress(p => {
      const next = { ...p };
      if (value === null) delete next[slot];
      else next[slot] = value;

      return next;
    });

  const upload = async (slot: GuideFixedSlot, file: File | undefined) => {
    if (!file || progress[slot] !== undefined) return;
    setSlotProgress(slot, 0);
    try {
      const added = await uploadCompressed(listingId, file, { slot }, pct => setSlotProgress(slot, pct));
      onMediaChange(cur => [...cur.filter(m => m.slot !== slot), added]);
      toast.success(`Photo saved for ${slotLabel(slot)}`);
    } catch (err) {
      toast.error(uploadErrorText(err));
    } finally {
      setSlotProgress(slot, null);
    }
  };

  const remove = async (slot: GuideFixedSlot) => {
    const current = media.find(m => m.slot === slot);
    if (!current) return;
    try {
      await deleteGuideMedia(listingId, current.id);
      onMediaChange(cur => cur.filter(m => m.id !== current.id));
      toast.success(`Photo removed from ${slotLabel(slot)}`);
    } catch (err) {
      toast.error(refusalText(err as StaysApiError, 'Could not remove the photo. Try again.'));
    }
  };

  return (
    <Section aria-label="Photos">
      <SlotGrid>
        {FIXED_SLOTS.map(slot => {
          const label = slotLabel(slot);
          const photo = media.find(m => m.slot === slot) || null;
          const pct = progress[slot];
          const inputId = `guide-photo-${slot}`;

          return (
            <SlotCard key={slot} data-slot={slot}>
              <SlotName>{label}</SlotName>
              {photo ? (
                <Frame>
                  <Thumb src={photo.url} alt={`Current photo for ${label}`} loading="lazy" />
                </Frame>
              ) : (
                <EmptyFrame>No photo yet</EmptyFrame>
              )}
              {isAfterCheckinSlot(slot) ? (
                <span>
                  <StatusPill tone="neutral">Shown after check-in is complete</StatusPill>
                </span>
              ) : null}
              {pct !== undefined ? (
                <Progress>
                  <ProgressTrack
                    role="progressbar"
                    aria-label={`Uploading photo for ${label}`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={pct}
                  >
                    <ProgressFill $percent={pct} />
                  </ProgressTrack>
                  <Muted>{`Uploading… ${pct}%`}</Muted>
                </Progress>
              ) : null}
              {canManage ? (
                <Row>
                  <FileButton htmlFor={inputId} $disabled={pct !== undefined}>
                    {`Upload photo for ${label}`}
                    <HiddenFile
                      id={inputId}
                      type="file"
                      accept={ACCEPT_PHOTOS}
                      disabled={pct !== undefined}
                      onChange={e => {
                        const file = e.target.files ? e.target.files[0] : undefined;
                        e.target.value = '';
                        void upload(slot, file);
                      }}
                    />
                  </FileButton>
                  {photo ? (
                    <AppButton variant="ghost" size="compact" disabled={pct !== undefined} onClick={() => setRemoving(slot)}>
                      {`Remove photo for ${label}`}
                    </AppButton>
                  ) : null}
                </Row>
              ) : null}
            </SlotCard>
          );
        })}
      </SlotGrid>
      <ConfirmDialog
        open={!!removing}
        title={removing ? `Remove the photo for ${slotLabel(removing)}?` : 'Remove photo?'}
        message="Guests stop seeing this photo in the guide at once."
        primaryLabel="Remove photo"
        primaryVariant="destructive"
        dismissLabel="Keep photo"
        onPrimary={async () => {
          if (removing) await remove(removing);
          setRemoving(null);
        }}
        onDismiss={() => setRemoving(null)}
      />
    </Section>
  );
};
